import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LuxuryButton from "../ui/LuxuryButton";
import { Reveal } from "../animations/Reveal";
import { designTokens } from '../../theme/designTokens';
import { Mail, Check, Gift, Sparkles, AlertCircle } from 'lucide-react';
import API_BASE from "../../api/api";

const NewsletterBit = () => {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || isLoading) return;

        // Reset states
        setError('');
        setSuccessMessage('');
        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE}/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                // Handle specific error cases
                if (response.status === 409) {
                    setError('This email is already subscribed.');
                } else if (response.status === 400) {
                    setError('Please enter a valid email address.');
                } else {
                    setError(data.message || 'Something went wrong. Please try again.');
                }
                setIsLoading(false);
                return;
            }

            // Success
            setIsLoading(false);
            setSubscribed(true);
            setSuccessMessage(data.message || 'Welcome to KROMAIC!');
            setEmail('');

            // Reset after 8 seconds (showing for longer)
            setTimeout(() => {
                setSubscribed(false);
                setSuccessMessage('');
            }, 8000);

        } catch (err) {
            console.error('Subscription error:', err);
            setError('Network error. Please check your connection and try again.');
            setIsLoading(false);
        }
    };

    const benefits = [
        { icon: <Gift size={20} />, text: "Welcome Gift" },
        { icon: <Sparkles size={20} />, text: "Exclusive Content" },
        { text: "Early Access" },
        { text: "Members-only Events" }
    ];

    return (
        <div style={{
            position: 'relative',
            borderRadius: '1.5rem',
            overflow: 'hidden',
            margin: '4rem auto',
            maxWidth: '1200px',
            width: '100%',
        }}>
            {/* Background with Gradient */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(135deg, ${designTokens.colors.charcoal} 0%, #2A2A2A 100%)`,
            }}>
                {/* Animated Grid Pattern */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.1,
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: '50px 50px',
                }} />

                {/* Floating Gradient Orbs */}
                <motion.div
                    style={{
                        position: 'absolute',
                        top: '-100px',
                        right: '-100px',
                        width: '300px',
                        height: '300px',
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${designTokens.colors.champagne}20 0%, transparent 70%)`,
                    }}
                    animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 180, 360],
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear",
                    }}
                />

                <motion.div
                    style={{
                        position: 'absolute',
                        bottom: '-80px',
                        left: '-80px',
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${designTokens.colors.sage}15 0%, transparent 70%)`,
                    }}
                    animate={{
                        scale: [1, 1.3, 1],
                        x: [0, 30, 0],
                        y: [0, -30, 0],
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1,
                    }}
                />
            </div>

            {/* Top Parallax Text with Gold Color Blur */}
            <motion.div
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    zIndex: 0,
                }}
            >
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '0 2rem',
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <span style={{
                            fontSize: 'clamp(3.5rem, 8vw, 8rem)',
                            fontFamily: designTokens.typography.fontFamilies.display,
                            fontWeight: 'bold',
                            letterSpacing: '0.05em',
                            background: `linear-gradient(to right, ${designTokens.colors.champagne}, ${designTokens.colors.champagne}80, ${designTokens.colors.champagne}60)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            filter: 'blur(3px)',
                            opacity: 0.3,
                            userSelect: 'none',
                        }}>
                            KROMAIC
                        </span>
                    </div>
                </div>
            </motion.div>

            {/* Main Content */}
            <div style={{
                position: 'relative',
                padding: '6rem 2rem',
                maxWidth: '800px',
                margin: '0 auto',
                textAlign: 'center',
                zIndex: 1,
            }}>
                <Reveal>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: 'rgba(201, 162, 77, 0.2)',
                        borderRadius: '9999px',
                        marginBottom: '2rem',
                    }}>
                        <Mail size={16} color={designTokens.colors.champagne} />
                        <span style={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            color: designTokens.colors.champagne,
                        }}>
                            Join Our World
                        </span>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <h2 style={{
                        fontFamily: designTokens.typography.fontFamilies.display,
                        fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
                        fontWeight: 300,
                        color: designTokens.colors.white,
                        lineHeight: 1.2,
                        marginBottom: '1.5rem',
                    }}>
                        The Art of{' '}
                        <span style={{
                            fontStyle: 'italic',
                            color: designTokens.colors.sage,
                        }}>
                            Informed
                        </span>{' '}
                        Beauty
                    </h2>
                </Reveal>

                <Reveal delay={0.4}>
                    <p style={{
                        fontSize: '1.125rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: 1.6,
                        maxWidth: '600px',
                        margin: '0 auto 2.5rem',
                    }}>
                        Receive exclusive insights on skincare science, mindful rituals,
                        and early access to new formulations. Plus, 15% off your first order.
                    </p>
                </Reveal>

                {/* Benefits */}
                <Reveal delay={0.6}>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '1rem',
                        marginBottom: '3rem',
                    }}>
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.625rem 1.25rem',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '9999px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                }}
                                whileHover={{
                                    scale: 1.05,
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                {benefit.icon && (
                                    <span style={{ color: designTokens.colors.champagne }}>
                                        {benefit.icon}
                                    </span>
                                )}
                                <span style={{
                                    fontSize: '0.875rem',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 500,
                                }}>
                                    {benefit.text}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </Reveal>

                {/* Error Message */}
                {error && (
                    <Reveal>
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '1rem 1.5rem',
                                backgroundColor: 'rgba(220, 38, 38, 0.2)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '0.75rem',
                                border: '1px solid rgba(220, 38, 38, 0.3)',
                                maxWidth: '500px',
                                margin: '0 auto 1.5rem',
                            }}
                        >
                            <AlertCircle size={20} color="#FCA5A5" />
                            <span style={{
                                fontSize: '0.875rem',
                                color: '#FECACA',
                                fontWeight: 500,
                            }}>
                                {error}
                            </span>
                        </motion.div>
                    </Reveal>
                )}

                {/* Subscription Form */}
                <Reveal delay={0.8}>
                    <motion.div
                        initial={false}
                        animate={subscribed ? {
                            scale: [1, 1.02, 1],
                        } : {}}
                        transition={{ duration: 0.5 }}
                    >
                        {subscribed ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '1.5rem',
                                    padding: '2rem 3rem',
                                    background: `linear-gradient(135deg, rgba(143, 175, 154, 0.2) 0%, rgba(201, 162, 77, 0.2) 100%)`,
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '1rem',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                }}
                            >
                                <div style={{
                                    width: '3rem',
                                    height: '3rem',
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${designTokens.colors.sage} 0%, ${designTokens.colors.champagne} 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Check size={24} color={designTokens.colors.white} />
                                </div>
                                <div style={{ textAlign: 'left' }}>
                                    <h3 style={{
                                        fontSize: '1.25rem',
                                        fontWeight: 600,
                                        color: designTokens.colors.white,
                                        marginBottom: '0.25rem',
                                    }}>
                                        {successMessage.split('.')[0] || 'Welcome to KROMAIC'}
                                    </h3>
                                    <p style={{
                                        fontSize: '0.875rem',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    }}>
                                        {successMessage.includes('Check your email')
                                            ? successMessage
                                            : 'Check your email to confirm and receive your welcome gift.'}
                                    </p>
                                </div>
                            </motion.div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                style={{
                                    maxWidth: '500px',
                                    margin: '0 auto',
                                }}
                            >
                                <div style={{
                                    position: 'relative',
                                    marginBottom: '1.5rem',
                                }}>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setError(''); // Clear error when user types
                                        }}
                                        placeholder="Enter your email address"
                                        required
                                        disabled={isLoading}
                                        style={{
                                            width: '100%',
                                            padding: '1.25rem 1.5rem',
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            backdropFilter: 'blur(10px)',
                                            border: error
                                                ? '1px solid rgba(220, 38, 38, 0.5)'
                                                : '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '3rem',
                                            fontSize: '1rem',
                                            color: designTokens.colors.white,
                                            outline: 'none',
                                            transition: 'all 0.3s ease',
                                            opacity: isLoading ? 0.7 : 1,
                                            cursor: isLoading ? 'not-allowed' : 'text',
                                        }}
                                        onFocus={(e) => {
                                            if (!isLoading) {
                                                e.target.style.borderColor = error
                                                    ? 'rgba(220, 38, 38, 0.7)'
                                                    : `${designTokens.colors.champagne}80`;
                                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                            }
                                        }}
                                        onBlur={(e) => {
                                            if (!isLoading) {
                                                e.target.style.borderColor = error
                                                    ? 'rgba(220, 38, 38, 0.5)'
                                                    : 'rgba(255, 255, 255, 0.1)';
                                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                            }
                                        }}
                                    />

                                    {/* Animated Focus Ring */}
                                    {email && !error && (
                                        <motion.div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                borderRadius: '3rem',
                                                border: `2px solid ${designTokens.colors.champagne}`,
                                                pointerEvents: 'none',
                                            }}
                                            initial={{ opacity: 0.5 }}
                                            animate={{
                                                opacity: [0.5, 0.8, 0.5],
                                                scale: [1, 1.02, 1],
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                            }}
                                        />
                                    )}

                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '1.5rem',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none',
                                        opacity: email ? 1 : 0.5,
                                        transition: 'opacity 0.3s ease',
                                    }}>
                                        <Mail size={20} color={error ? '#FCA5A5' : 'rgba(255, 255, 255, 0.5)'} />
                                    </div>
                                </div>

                                <LuxuryButton
                                    type="submit"
                                    variant="primary"
                                    size="large"
                                    fullWidth
                                    isLoading={isLoading}
                                    disabled={isLoading || !email}
                                    icon={!isLoading && (
                                        <span style={{ fontSize: '1.25rem' }}>→</span>
                                    )}
                                    iconPosition="right"
                                    style={{
                                        background: `linear-gradient(135deg, ${designTokens.colors.champagne} 0%, ${designTokens.colors.sage} 100%)`,
                                        marginBottom: '1.5rem',
                                        opacity: isLoading || !email ? 0.8 : 1,
                                        cursor: isLoading || !email ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {isLoading ? 'Subscribing...' : 'Begin Your Ritual'}
                                </LuxuryButton>

                                <p style={{
                                    fontSize: '0.75rem',
                                    color: 'rgba(255, 255, 255, 0.4)',
                                    lineHeight: 1.5,
                                }}>
                                    By subscribing, you agree to our Privacy Policy and consent to
                                    receive updates from KROMAIC. You can unsubscribe at any time.
                                </p>
                            </form>
                        )}
                    </motion.div>
                </Reveal>
            </div>

            {/* Floating Elements */}
            <motion.div
                style={{
                    position: 'absolute',
                    top: '2rem',
                    left: '2rem',
                    width: '4rem',
                    height: '4rem',
                    borderRadius: '50%',
                    border: `1px solid rgba(201, 162, 77, 0.3)`,
                    pointerEvents: 'none',
                }}
                animate={{
                    rotate: 360,
                }}
                transition={{
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear",
                }}
            />

            <motion.div
                style={{
                    position: 'absolute',
                    bottom: '2rem',
                    right: '2rem',
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(143, 175, 154, 0.1)',
                    pointerEvents: 'none',
                }}
                animate={{
                    scale: [1, 1.2, 1],
                }}
                transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />
        </div>
    );
};

export default NewsletterBit;